import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Save, X } from 'lucide-react';
import React, { useState } from 'react';
import { useToast } from '../hooks/useToast';
import { partnerApi, type Tenant } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface EditTenantFormProps {
  tenant: Tenant;
  onCancel: () => void;
  onSuccess: () => void;
}

export const EditTenantForm: React.FC<EditTenantFormProps> = ({ tenant, onCancel, onSuccess }) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [formData, setFormData] = useState({
    name: tenant.name,
    website: tenant.website || '',
    email: tenant.branding?.primaryEmail || '',
    smsOptInImageUrls: tenant.smsOptInImageUrls || [],
  });

  const updateMutation = useMutation({
    mutationFn: (data: {
      name: string;
      website?: string;
      smsOptInImageUrls?: string[];
      branding?: {
        primaryEmail?: string | null;
      };
    }) => partnerApi.updateTenant(tenant.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      addToast({
        variant: 'success',
        title: 'Tenant updated successfully',
      });
      onSuccess();
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to update tenant',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: {
      name: string;
      website?: string;
      smsOptInImageUrls?: string[];
      branding?: {
        primaryEmail?: string | null;
      };
    } = {
      name: formData.name,
    };

    if (formData.website) {
      submitData.website = formData.website;
    }

    if (formData.smsOptInImageUrls.length > 0) {
      submitData.smsOptInImageUrls = formData.smsOptInImageUrls;
    }

    // Only include branding if email has a value
    if (formData.email && formData.email.trim() !== '') {
      submitData.branding = {
        primaryEmail: formData.email,
      };
    }

    updateMutation.mutate(submitData);
  };

  const addSmsUrl = () => {
    setFormData({
      ...formData,
      smsOptInImageUrls: [...formData.smsOptInImageUrls, ''],
    });
  };

  const removeSmsUrl = (index: number) => {
    const newUrls = formData.smsOptInImageUrls.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      smsOptInImageUrls: newUrls,
    });
  };

  const updateSmsUrl = (index: number, value: string) => {
    const newUrls = [...formData.smsOptInImageUrls];
    newUrls[index] = value;
    setFormData({
      ...formData,
      smsOptInImageUrls: newUrls,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Tenant Name</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          type="url"
          value={formData.website}
          onChange={e => setFormData({ ...formData, website: e.target.value })}
          placeholder="https://example.com"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Tenant Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={e => setFormData({ ...formData, email: e.target.value })}
          placeholder="contact@example.com"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>SMS Opt-in Image URLs</Label>
          <Button type="button" variant="outline" size="sm" onClick={addSmsUrl} className="h-8">
            <Plus className="w-4 h-4 mr-1" />
            Add URL
          </Button>
        </div>
        <p className="text-sm text-gray-500">
          URLs showing how customers consent to receive SMS messages (required for Twilio SMS
          registration approval)
        </p>
        {formData.smsOptInImageUrls.length === 0 ? (
          <div className="text-sm text-gray-400 italic py-2">
            No SMS opt-in URLs configured. SMS registration applications will not be auto-submitted.
          </div>
        ) : (
          <div className="space-y-2">
            {formData.smsOptInImageUrls.map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="url"
                  value={url}
                  onChange={e => updateSmsUrl(index, e.target.value)}
                  placeholder="https://example.com/consent-page or image URL"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeSmsUrl(index)}
                  className="h-10 px-3">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={updateMutation.isPending}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button type="submit" disabled={updateMutation.isPending}>
          {updateMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
